import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { useState } from 'react';

export type GroupByOption = 'none' | 'records_request_status' | 'matter' | 'records_request_status_and_matter' | 'matter_and_provider' | 'records_request_status_and_matter_and_provider';

interface GroupByOption_Config {
  id: GroupByOption;
  label: string;
  description?: string;
}

const GROUP_BY_OPTIONS: GroupByOption_Config[] = [
  { id: 'none', label: 'No grouping' },
  {
    id: 'records_request_status',
    label: 'Records Request Status',
    description: 'Group by record request status',
  },
  { id: 'matter', label: 'Matter', description: 'Group by matter/case' },
  {
    id: 'matter_and_provider',
    label: 'Matter + Medical Provider',
    description: 'Group by matter, then by medical provider',
  },
  {
    id: 'records_request_status_and_matter',
    label: 'Records Request Status + Matter',
    description: 'Group by record request status, then by matter',
  },
  {
    id: 'records_request_status_and_matter_and_provider',
    label: 'Status + Matter + Medical Provider',
    description: 'Group by record request status, matter, then provider',
  },
];

interface GroupBySelectorProps {
  value: GroupByOption;
  onChange: (value: GroupByOption) => void;
}

/**
 * Dropdown selector for grouping tasks in the task list.
 * Currently used for Medical Records to group by Records Request status and/or matter.
 */
export const GroupBySelector = ({ value, onChange }: GroupBySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = GROUP_BY_OPTIONS.find((opt) => opt.id === value);
  const isActive = value !== 'none';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
          isActive
            ? 'text-zinc-950 dark:text-white bg-zinc-100 dark:bg-zinc-800'
            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
        }`}
      >
        <Squares2X2Icon className="h-4 w-4 shrink-0" />
        <span>
          {isActive ? `Group: ${selectedOption?.label}` : 'Group by'}
        </span>
        <ChevronDownIcon className="size-3" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close group by dropdown"
          />

          {/* Dropdown menu */}
          <div className="absolute left-0 mt-1 w-56 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-md shadow-lg z-20">
            {GROUP_BY_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id);
                  setIsOpen(false);
                }}
                className={`
                  w-full text-left px-3 py-2 text-xs
                  ${option.id === value ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-700'}
                  transition-colors
                  first:rounded-t-md last:rounded-b-md
                `.trim()}
                title={option.description}
              >
                <div>{option.label}</div>
                {option.description && (
                  <div className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-0.5">
                    {option.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
